import type { ReactNode } from 'react';
import {
  DecoratorNode,
  type DOMConversionMap,
  type DOMConversionOutput,
  type DOMExportOutput,
  type LexicalNode,
  type NodeKey,
  type SerializedLexicalNode,
  type Spread,
} from 'lexical';
import {
  buildAgentRunReference,
  buildContextReference,
  parseContextReferences,
} from '@runloop/reflex-contract';
import { AgentRunBadge } from '@reflex/ui/components/shared/AgentRunBadge';
import { ContextRefBadge } from '@reflex/ui/components/shared/ContextRefBadge';
import { getContextMentionRenderer } from '@reflex/ui/client/context-mention-renderers';

/**
 * Kinds of inline reference a {@link MentionNode} can represent: another
 * agent run, or a plugin context item (a pull request, a Linear ticket, …)
 * whose `contextKind` names the server-side resolver. The node is the
 * reusable atom for "context cards in the composer" — new kinds add a `kind`
 * + a `decorate`/`getTextContent` branch.
 */
export type MentionData =
  | { kind: 'agent-run'; id: string; title: string }
  | { kind: 'context'; contextKind: string; id: string; title: string };

export type SerializedMentionNode = Spread<{ mention: MentionData }, SerializedLexicalNode>;

/** Serialize a mention to the plain-text marker the server expands. */
export function mentionToText(mention: MentionData): string {
  switch (mention.kind) {
    case 'agent-run':
      return buildAgentRunReference(mention.id, mention.title);
    case 'context':
      return buildContextReference(mention.contextKind, mention.id, mention.title);
  }
}

/**
 * An atomic, inline "context card" in the composer. It renders as a badge but
 * behaves like a single character: the caret can't enter it, Backspace deletes
 * the whole thing, and copy/paste carries it as one unit. Its text content is
 * the marker (`@[title](agent-run:id)`), so the serialized prompt and any
 * paste-as-text round-trip cleanly.
 */
export class MentionNode extends DecoratorNode<ReactNode> {
  __mention: MentionData;

  static getType(): string {
    return 'mention';
  }

  static clone(node: MentionNode): MentionNode {
    return new MentionNode(node.__mention, node.__key);
  }

  constructor(mention: MentionData, key?: NodeKey) {
    super(key);
    this.__mention = mention;
  }

  static importJSON(json: SerializedMentionNode): MentionNode {
    return new MentionNode(json.mention);
  }

  exportJSON(): SerializedMentionNode {
    return { ...super.exportJSON(), type: 'mention', version: 1, mention: this.__mention };
  }

  // Pasting the badge as plain text (or into another app) yields the marker.
  exportDOM(): DOMExportOutput {
    const element = document.createElement('span');
    element.setAttribute('data-mention', mentionToText(this.__mention));
    element.textContent = mentionToText(this.__mention);
    return { element };
  }

  static importDOM(): DOMConversionMap | null {
    return {
      span: (el: HTMLElement) =>
        el.hasAttribute('data-mention') ? { conversion: convertMentionSpan, priority: 1 } : null,
    };
  }

  // Make `$getRoot().getTextContent()` (and clipboard plain text) emit the marker.
  getTextContent(): string {
    return mentionToText(this.__mention);
  }

  isInline(): boolean {
    return true;
  }

  isKeyboardSelectable(): boolean {
    return true;
  }

  createDOM(): HTMLElement {
    const span = document.createElement('span');
    // `select-all` makes the chip an atomic selection unit, so dragging a
    // selection over it highlights the whole badge (not just the inner text).
    // Copy still serializes to the marker via Lexical's clipboard handler.
    span.className = 'inline-flex select-all align-middle';
    return span;
  }

  updateDOM(): boolean {
    return false;
  }

  decorate(): ReactNode {
    if (this.__mention.kind === 'agent-run') {
      return <AgentRunBadge agentId={this.__mention.id} title={this.__mention.title} />;
    }
    if (this.__mention.kind === 'context') {
      const CustomBadge = getContextMentionRenderer(this.__mention.contextKind);
      if (CustomBadge) {
        return (
          <CustomBadge
            contextKind={this.__mention.contextKind}
            refId={this.__mention.id}
            title={this.__mention.title}
          />
        );
      }
      return (
        <ContextRefBadge
          contextKind={this.__mention.contextKind}
          refId={this.__mention.id}
          title={this.__mention.title}
        />
      );
    }
    return null;
  }
}

function convertMentionSpan(el: HTMLElement): DOMConversionOutput {
  const text = el.getAttribute('data-mention') ?? '';
  const mention = parseMentionMarker(text);
  return { node: mention ? new MentionNode(mention) : null };
}

const AGENT_RUN_MARKER = /@\[([^\]\n]*)\]\(agent-run:([A-Za-z0-9_:.-]+)\)/;

/** Parse a marker string into mention data (used when pasting marker text). */
export function parseMentionMarker(text: string): MentionData | null {
  const m = text.match(AGENT_RUN_MARKER);
  if (m) return { kind: 'agent-run', id: m[2], title: m[1] };
  const [ctx] = parseContextReferences(text);
  if (ctx) return { kind: 'context', contextKind: ctx.kind, id: ctx.id, title: ctx.title };
  return null;
}

export function $createMentionNode(mention: MentionData): MentionNode {
  return new MentionNode(mention);
}

export function $isMentionNode(node: LexicalNode | null | undefined): node is MentionNode {
  return node instanceof MentionNode;
}
